// ProblemAlert (Parte 14.4/14.5): ProblemDetails padronizado — título localizado, errorCode traduzido de errors.json,
// correlationId visível, ação de repetir; 423 mostra o motivo (trava) e 404/403 de escopo não revelam existência.
import { GiyusApiError, GiyusNetworkError } from '@giyus/api-client';
import { Alert, AlertDescription, AlertTitle, Button } from '@giyus/ui';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

export interface ProblemAlertProps {
  readonly error: unknown;
  readonly onRetry?: () => void;
  /** Caminho de exceção (423 com exceção possível). */
  readonly onRequestException?: () => void;
  readonly exceptionLabel?: string;
}

function describe(
  error: unknown,
  t: TFunction<['common', 'errors']>,
): { title: string; message: string; correlationId: string | null } {
  if (error instanceof GiyusApiError) {
    const message = t(`errors:codes.${error.errorCode}`, {
      defaultValue: t('errors:generic.unknown', { errorCode: error.errorCode }),
    });
    return {
      title: error.problem.title ?? t('common:components.problem.title'),
      message: error.isOutOfScope ? t('common:components.problem.outOfScope') : message,
      correlationId: error.correlationId ?? null,
    };
  }
  if (error instanceof GiyusNetworkError) {
    return {
      title: t('common:components.problem.title'),
      message: t('errors:generic.network'),
      correlationId: error.correlationId ?? null,
    };
  }
  return {
    title: t('common:components.problem.title'),
    message: t('errors:generic.unknown', { errorCode: 'PLT_UNEXPECTED' }),
    correlationId: null,
  };
}

export function ProblemAlert({
  error,
  onRetry,
  onRequestException,
  exceptionLabel,
}: ProblemAlertProps) {
  const { t } = useTranslation(['common', 'errors']);
  const { title, message, correlationId } = describe(error, t);
  const api = error instanceof GiyusApiError ? error : null;

  return (
    <Alert variant="destructive" role="alert" data-testid="problem-alert">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <p>{message}</p>
        {api?.isLocked && <p>{t('common:components.problem.locked')}</p>}
        {api && api.blockingConstraints.length > 0 && (
          <div>
            <p className="font-medium">{t('common:components.problem.blockingConstraints')}</p>
            <ul className="list-disc pl-5">
              {api.blockingConstraints.map((constraint, index) => (
                <li key={`${constraint.code}-${String(index)}`}>
                  {constraint.detail ?? constraint.code}
                </li>
              ))}
            </ul>
          </div>
        )}
        {api && api.fieldErrors.length > 0 && (
          <div>
            <p className="font-medium">{t('common:components.problem.fieldErrors')}</p>
            <ul className="list-disc pl-5">
              {api.fieldErrors.map((fieldError) => (
                <li key={`${fieldError.field}-${fieldError.code}`}>
                  {fieldError.field}: {fieldError.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        {correlationId && (
          <p className="font-mono text-caption" data-testid="problem-correlation-id">
            {t('common:state.correlationId', { id: correlationId })}
          </p>
        )}
        {(onRetry ?? onRequestException) && (
          <div className="flex gap-2 pt-1">
            {onRetry && (
              <Button size="sm" variant="outline" onClick={onRetry}>
                {t('common:actions.retry')}
              </Button>
            )}
            {onRequestException && api?.isLocked && (
              <Button size="sm" variant="secondary" onClick={onRequestException}>
                {exceptionLabel ?? t('common:components.exception.label')}
              </Button>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
